import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, ClipboardCheck, Wifi, WifiOff, RotateCcw, Trash2, CheckCircle } from 'lucide-react';
import { useOfflineQueue } from '../hooks/useOfflineQueue';

const ESTADO_CFG = {
  pendiente: { label: 'Pendiente', bg: '#1C1200', color: '#F59E0B' },
  enviando:  { label: 'Enviando…', bg: '#0D1E2E', color: '#38BDF8' },
  error:     { label: 'Error',     bg: '#2D0F0F', color: '#EF4444' },
};

export default function Pendientes() {
  const navigate = useNavigate();
  const { cola, total, procesarCola, reintentar, eliminar } = useOfflineQueue();
  const hayErrores = cola.some(i => i.estado === 'error');
  const estaOnline = navigator.onLine;

  return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <h1>Pendientes de envío</h1>
      </div>

      <div className="page-content">

        {/* Banner conexión */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: estaOnline ? '#12382D' : '#1C1200',
          border: `1px solid ${estaOnline ? 'rgba(79,175,80,0.25)' : '#3D2B00'}`,
          borderRadius: 12, padding: '11px 14px', marginBottom: 16,
        }}>
          {estaOnline
            ? <Wifi size={16} color="#4CAF50" strokeWidth={2} />
            : <WifiOff size={16} color="#F59E0B" strokeWidth={2} />
          }
          <span style={{ fontSize: 13, fontWeight: 600,
                         color: estaOnline ? '#4CAF50' : '#F59E0B' }}>
            {estaOnline
              ? 'Con conexión — los envíos se procesan automáticamente'
              : 'Sin conexión — los formularios se guardan aquí'}
          </span>
        </div>

        {/* Sin pendientes */}
        {total === 0 && (
          <div style={{
            textAlign: 'center', padding: '52px 20px',
            background: '#161C19', borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, background: '#12382D',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <CheckCircle size={26} color="#4CAF50" strokeWidth={1.75} />
            </div>
            <p style={{ fontWeight: 700, fontSize: 15, color: '#F4F7F5', marginBottom: 6 }}>
              Todo al día
            </p>
            <p style={{ color: '#8A9994', fontSize: 13 }}>
              No hay comprobantes pendientes de envío
            </p>
          </div>
        )}

        {/* Botón reintentar todo */}
        {hayErrores && estaOnline && (
          <button className="btn btn-primary" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={procesarCola}>
            <RotateCcw size={15} strokeWidth={2} />
            Reintentar todos los fallidos
          </button>
        )}

        {/* Lista */}
        {cola.map(item => {
          const cfg = ESTADO_CFG[item.estado] || ESTADO_CFG.pendiente;
          const fecha = item.payload?.fecha_visita || item.fecha_creacion?.slice(0, 10);
          const esBorrador = item.estado === 'error';
          const esMant = item.tipo === 'mantenimiento';

          return (
            <div key={item.id} style={{
              background: '#161C19', borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.07)',
              padding: 14, marginBottom: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: esMant ? '#12382D' : '#0D1E2E',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {esMant
                        ? <Wrench size={16} color="#4CAF50" strokeWidth={2} />
                        : <ClipboardCheck size={16} color="#60A5FA" strokeWidth={2} />
                      }
                    </div>
                    <div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#F4F7F5' }}>
                        {item.ascensor?.comunidad || '—'}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: '#8A9994' }}>
                        Ascensor {item.ascensor?.n_ascensor} — Torre {item.ascensor?.torre}
                      </p>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#6B7C74' }}>
                    {esMant ? 'Mantenimiento' : 'Visita técnica'} · {fecha}
                  </p>
                </div>
                <span style={{
                  background: cfg.bg, color: cfg.color,
                  padding: '3px 10px', borderRadius: 20,
                  fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                }}>{cfg.label}</span>
              </div>

              {item.error_msg && (
                <p style={{
                  margin: '6px 0 0', fontSize: 12, color: '#EF4444',
                  background: '#2D0F0F', padding: '6px 10px', borderRadius: 8,
                }}>{item.error_msg}</p>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                {esBorrador && estaOnline && (
                  <button onClick={() => reintentar(item.id)} style={{
                    flex: 1, padding: '8px 10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: '#12382D', border: '1px solid rgba(79,175,80,0.2)',
                    borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', color: '#4CAF50', fontFamily: 'inherit',
                  }}>
                    <RotateCcw size={13} strokeWidth={2} />
                    Reintentar
                  </button>
                )}
                <button onClick={() => {
                  if (window.confirm('¿Eliminar este comprobante pendiente?')) eliminar(item.id);
                }} style={{
                  flex: 1, padding: '8px 10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: '#2D0F0F', border: '1px solid rgba(198,40,40,0.2)',
                  borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', color: '#EF4444', fontFamily: 'inherit',
                }}>
                  <Trash2 size={13} strokeWidth={2} />
                  Eliminar
                </button>
              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
}
